import React, { useMemo } from 'react';
import { ImagePickerSettings } from '@modules/proposal/ImagePickerSettings';
import { Label, Row, Col } from 'reactstrap';
import { CategoryId } from '@business/entities/category';
import { ItemConsiderationCategoryImageFormData } from '../ItemConsiderationBlock';

interface CategoryImageSettingsProps {
  categoryId: CategoryId;
  categoryImageData: ItemConsiderationCategoryImageFormData;
  updateCategoryImage: (categoryId: CategoryId, imageData: Partial<ItemConsiderationCategoryImageFormData>) => void;
  categoryIndex: number;
}

export const CategoryImageSettings = (props: CategoryImageSettingsProps) => {
  const { categoryId, categoryImageData, updateCategoryImage, categoryIndex } = props;

  // Memoize position options for performance
  const positionOptions = useMemo(
    () => [
      { key: 'left', label: 'Left' },
      { key: 'right', label: 'Right' },
      { key: 'top', label: 'Top' },
      { key: 'bottom', label: 'Bottom' },
      { key: 'overlay', label: 'Text Overlay' },
    ],
    [],
  );

  const widthOptions = useMemo(() => ['25%', '50%', '75%'], []);

  const handlePositionChange = (position: 'left' | 'right' | 'top' | 'bottom' | 'overlay') => {
    updateCategoryImage(categoryId, { imagePosition: position });
  };

  const handleWidthChange = (width: string) => {
    updateCategoryImage(categoryId, { imageWidth: width });
  };

  return (
    <div className="mb-3">
      <Label>Category Image (Optional)</Label>
      <ImagePickerSettings
        onSelect={imageUrl => updateCategoryImage(categoryId, { imageUrl })}
        currentImageUrl={categoryImageData.imageUrl}
        path="proposal/categories"
      />

      {categoryImageData.imageUrl && (
        <div className="mt-3">
          <Row>
            <Col md={6}>
              <Label>Image Position</Label>
              <div className="btn-group-vertical d-block">
                <div className="btn-group mb-2">
                  <button
                    type="button"
                    className={`btn btn-sm ${String(categoryImageData.imagePosition || 'left') === 'left' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handlePositionChange('left')}
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${String(categoryImageData.imagePosition || 'left') === 'right' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handlePositionChange('right')}
                  >
                    Right
                  </button>
                </div>
                <div className="btn-group mb-2">
                  <button
                    type="button"
                    className={`btn btn-sm ${String(categoryImageData.imagePosition || 'left') === 'top' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handlePositionChange('top')}
                  >
                    Top
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${String(categoryImageData.imagePosition || 'left') === 'bottom' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handlePositionChange('bottom')}
                  >
                    Bottom
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${String(categoryImageData.imagePosition || 'left') === 'overlay' ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => handlePositionChange('overlay')}
                  >
                    Text Overlay
                  </button>
                </div>
              </div>
            </Col>
            <Col md={6}>
              {categoryImageData.imagePosition !== 'overlay' && (
                <div>
                  <Label>Image Width</Label>
                  <div className="btn-group">
                    {widthOptions.map(width => (
                      <button
                        key={width}
                        type="button"
                        className={`btn btn-sm ${String(categoryImageData.imageWidth || '50%') === width ? 'btn-primary' : 'btn-light'}`}
                        onClick={() => handleWidthChange(width)}
                      >
                        {width}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {categoryImageData.imagePosition === 'overlay' && (
                <div>
                  <Label>Text Color</Label>
                  <input
                    type="color"
                    className="form-control mb-2"
                    value={categoryImageData.overlayTextColor || '#000000'}
                    onChange={e => updateCategoryImage(categoryId, { overlayTextColor: e.target.value })}
                  />
                  <Label>Background Opacity</Label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={categoryImageData.overlayBackgroundOpacity || 0.5}
                    onChange={e => updateCategoryImage(categoryId, { overlayBackgroundOpacity: parseFloat(e.target.value) })}
                  />
                  <small className="text-muted">{Math.round((categoryImageData.overlayBackgroundOpacity || 0.5) * 100)}%</small>
                </div>
              )}
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};
